import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  MenuItem,
} from "@mui/material";
import { useAddTagMutation, useFetchTags, useUpdateTagMutation } from "../../../hooks/tag-management-hooks";
import { Tag } from "../../../types/tag";
import AdminConfirmDialog from "../Common/AdminConfirmDialog";

interface TagForm {
  key: string;
  value: string;
  parent: number | null;
}

type Props = {
  open: boolean;
  tag?: Tag;
  onClose: () => void;
  onSaved: (newTagId?: number) => void;

  /** NEW: view-only mode */
  readOnly?: boolean;
};

export default function TagEditorDialog({
                                          open,
                                          tag,
                                          onClose,
                                          onSaved,
                                          readOnly = false,
                                        }: Props) {
  const { data: tags, isLoading: tagsLoading } = useFetchTags({ enabled: open && !readOnly });
  const { mutateAsync: addTag, isPending: addingTag } = useAddTagMutation();
  const { mutateAsync: updateTag, isPending: updatingTag } = useUpdateTagMutation();

  const isEditOrViewTag = !!tag;

  const [form, setForm] = useState<TagForm>({
    key: "",
    value: "",
    parent: null,
  });

  const [initialForm, setInitialForm] = useState<TagForm>({
    key: "",
    value: "",
    parent: null,
  });

  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  useEffect(() => {
    if (!open) return;

    if (isEditOrViewTag) {
      const { key = "", value = "", parent = null } = tag;
      const nextForm = { key, value, parent };
      setForm(nextForm);
      setInitialForm(nextForm);
    } else {
      const emptyForm = { key: "", value: "", parent: null };
      setForm(emptyForm);
      setInitialForm(emptyForm);
    }
  }, [open, isEditOrViewTag, tag]);

  const hasUnsavedChanges = useMemo(() => {
    return (
      form.key !== initialForm.key ||
      form.value !== initialForm.value ||
      form.parent !== initialForm.parent
    );
  }, [form, initialForm]);

  // Filter out self and descendants to prevent circular references
  const availableParents: Tag[] = useMemo(() => {
    if (!tags) return [];
    if (!isEditOrViewTag) return tags;

    const parentMap = tags.reduce((acc, t) => {
      acc[t.parent] = [...(acc[t.parent] ?? []), t];
      return acc;
    }, {} as Record<Tag["parent"], Tag[]>);

    const descendantIds = new Set<Tag["id"]>();
    const findDescendants = (parentId: Tag["id"]) => {
      descendantIds.add(parentId);
      for (const child of parentMap[parentId] ?? []) {
        if (!descendantIds.has(child.id)) {
          findDescendants(child.id);
        }
      }
    };
    findDescendants(tag.id);

    return tags.filter((t: Tag) => !descendantIds.has(t.id));
  }, [tags, isEditOrViewTag, tag]);

  // Group tags by key for easier selection
  const tagsByKey = useMemo(() => {
    const grouped: Record<Tag["key"] | "Uncategorized", Tag[]> = {};
    availableParents.forEach((tag: Tag) => {
      const key = tag.key || "Uncategorized";
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(tag);
    });
    return grouped;
  }, [availableParents]);

  // Get children count
  const childrenCount = useMemo(() => {
    if (!isEditOrViewTag || !tags) return 0;
    return tags.filter((t: Tag) => t.parent === tag.id).length;
  }, [tags, isEditOrViewTag, tag]);

  const saveTag = async () => {
    if (readOnly) {
      onClose();
      return;
    }

    const payload = {
      ...form,
      parent: form.parent || null,
    };

    try {
      if (!isEditOrViewTag) {
        const created = await addTag(payload);
        const newId = created?.id;

        onClose();

        if (onSaved) {
          setTimeout(() => {
            onSaved(newId);
          }, 0);
        }
        return;
      }

      if (!tag) return;
      await updateTag({ ...payload, id: tag.id });

      onClose();

      if (onSaved) {
        setTimeout(() => {
          onSaved(undefined);
        }, 0);
      }
    } catch (error) {
      console.error("Failed to save tag:", error);
    }
  };

  const resetAndClose = () => {
    setForm({ key: "", value: "", parent: null });
    setInitialForm({ key: "", value: "", parent: null });
    setConfirmCloseOpen(false);
    onClose();
  };

  const handleClose = () => {
    if (readOnly || !hasUnsavedChanges) {
      resetAndClose();
      return;
    }
    setConfirmCloseOpen(true);
  };

  const handleCancelClose = () => {
    setConfirmCloseOpen(false);
  };

  const handleConfirmClose = () => {
    resetAndClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {readOnly
            ? !isEditOrViewTag
              ? "View Tag"
              : `View Tag: ${form?.key || `#${tag?.id}`}`
            : !isEditOrViewTag
              ? "Create Tag"
              : `Edit Tag: ${form?.key || `#${tag?.id}`}`}
        </DialogTitle>

        <DialogContent>
          <Box>
            <TextField
              fullWidth
              margin="normal"
              label="Key"
              required
              value={form.key ?? ""}
              onChange={(e) => !readOnly && setForm((p: any) => ({ ...p, key: e.target.value }))}
              disabled={readOnly}
            />

            <TextField
              fullWidth
              margin="normal"
              label="Value"
              required
              value={form.value ?? ""}
              onChange={(e) => !readOnly && setForm((p: any) => ({ ...p, value: e.target.value }))}
              helperText="E.g., Category, Platform, Technology, etc."
              disabled={readOnly}
            />

            <TextField
              select
              fullWidth
              margin="normal"
              label={readOnly && !form.parent ? "No parent assigned" : "Parent Tag"}
              value={form.parent ?? ""}
              onChange={(e) =>
                !readOnly &&
                setForm((p: any) => ({
                  ...p,
                  parent: e.target.value ? Number(e.target.value) : null,
                }))
              }
              helperText={!readOnly ? "Optional: Set a parent to create a hierarchy" : undefined}
              disabled={readOnly}
            >
              <MenuItem value="">
                <em>None (Top Level)</em>
              </MenuItem>
              {Object.entries(tagsByKey)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([key, tags]) => [
                  <MenuItem key={`header-${key}`} disabled>
                    <Typography variant="caption" fontWeight={600}>
                      {key}
                    </Typography>
                  </MenuItem>,
                  ...tags.map((tag: any) => (
                    <MenuItem key={tag.id} value={tag.id}>
                      &nbsp;&nbsp;{tag.key}:{tag.value}
                    </MenuItem>
                  )),
                ])}
            </TextField>

            {isEditOrViewTag && !readOnly && childrenCount > 0 && (
              <Box mt={2} p={2} bgcolor="info.light" borderRadius={1}>
                <Typography variant="body2">
                  This tag has <strong>{childrenCount}</strong> child tag(s). Deleting this tag will
                  set their parent to null.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>{readOnly ? "Close" : "Cancel"}</Button>
          {!readOnly && (
            <Button
              variant="contained"
              onClick={saveTag}
              disabled={readOnly || !form.key || addingTag || updatingTag}
            >
              Save
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <AdminConfirmDialog
        open={confirmCloseOpen}
        title="Discard Changes"
        message={
          <Box>
            <Typography variant="body1" gutterBottom>
              You have unsaved changes in this tag editor.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              If you continue, your changes will be lost.
            </Typography>
          </Box>
        }
        confirmLabel="Discard Changes"
        cancelLabel="Continue Editing"
        confirmColor="warning"
        loading={addingTag || updatingTag}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      />
    </>
  );
}